import React, { useState, useEffect, useContext } from 'react';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import IndeterminateCheckbox from 'components/common/IndeterminateCheckbox';
import { authContext } from 'contexts/AuthContext';
import { syncUserPermissions } from 'utils/permissions';
import { APP_TOKEN } from '../../../../constants';
import './roles-mobile.css';

const MENU_DEFINITIONS = [
  { label: 'Dashboard', modules: ['ADMIN_DASHBOARD', 'DASHBOARD'] },
  { label: 'Biddings', modules: ['BID', 'BIDDING'] },
  {
    label: 'Orders',
    modules: [
      'ORDER',
      'ORDER_LOGISTIC',
      'ORDER_LOGISTICS',
      'ORDER_PAYMENT_TRANSITION',
      'ORDER_PAYMENT_TRANSACTION',
      'PACKAGING_TYPE',
      'FOREIGN_DELIVERY_TERM',
      'FOREIGN_PAYMENT_TERM',
      'FORWARD_PAYMENT_TERM',
    ],
  },
  { label: 'Products', modules: ['CATEGORY', 'GRADE'] },
  { label: 'Invoices', modules: ['INVOICE'] },
  { label: 'Vendor', modules: ['TRADER'] },
  { label: 'Staff', modules: ['USER', 'ROLE', 'PERMISSION', 'ADDRESS'] },
  { label: 'Market Listing', modules: ['OFFER', 'DEMAND'] },
  {
    label: 'Application UI',
    modules: ['APP_UI_SETTING', 'APP_UI_SETTINGS', 'BANNER', 'FAQ'],
  },
  { label: 'Notification', modules: ['NOTIFICATION'] },
  { label: 'Subscription Plans', modules: ['SUBSCRIPTION'] },
  { label: 'Coupons', modules: ['COUPON'] },
  { label: 'Task Management', modules: ['TASK'] },
  { label: 'Settings', modules: ['APP_SETTING', 'APP_SETTINGS'] },
  {
    label: 'Extra',
    modules: ['MARKET_FREEZE', 'MARKET_FREEZE_ENTRY', 'PORT', 'CRM'],
  },
  {
    label: 'User Feedback Hubs',
    modules: ['USER_FEEDBACK', 'FEEDBACK', 'FEEDBACK_HUB'],
  },
  { label: 'Analytics', modules: ['ANALYTICS'] },
];

const MENU_ORDER = [...MENU_DEFINITIONS.map((item) => item.label), 'Other'];
const DEPARTMENT_OPTIONS = [
  { label: 'Logistics', value: 'LOGISTICS' },
  { label: 'Accounts', value: 'ACCOUNTS' },
  { label: 'Sales', value: 'SALES' },
  { label: 'BDE', value: 'BDE' },
  { label: 'Purchase', value: 'PURCHASE' },
];
const HIDDEN_DASHBOARD_PERMISSION_NAMES = new Set([
  // Dashboard permissions are now visible in UI
]);

const normalizeModuleName = (value) => `${value || ''}`.trim().toUpperCase();
const normalizePermissionName = (value) =>
  `${value || ''}`.trim().toUpperCase();
const shouldHidePermission = (permissionName) =>
  HIDDEN_DASHBOARD_PERMISSION_NAMES.has(
    normalizePermissionName(permissionName),
  );

const resolveMenuForModule = (moduleName) => {
  const normalized = normalizeModuleName(moduleName);
  const directMatch = MENU_DEFINITIONS.find((group) =>
    group.modules.includes(normalized),
  );
  if (directMatch) return directMatch.label;

  const fallbackRules = [
    {
      label: 'Orders',
      test: (name) => name === 'ORDER' || name.startsWith('ORDER_'),
    },
    {
      label: 'Application UI',
      test: (name) =>
        name.startsWith('APP_UI') ||
        name.includes('BANNER') ||
        name.includes('FAQ'),
    },
    { label: 'Settings', test: (name) => name.includes('APP_SETTING') },
    {
      label: 'Market Listing',
      test: (name) => name.includes('OFFER') || name.includes('DEMAND'),
    },
    {
      label: 'Subscription Plans',
      test: (name) => name.includes('SUBSCRIPTION'),
    },
    { label: 'Coupons', test: (name) => name.includes('COUPON') },
    { label: 'Task Management', test: (name) => name.includes('TASK') },
    { label: 'Notification', test: (name) => name.includes('NOTIFICATION') },
    {
      label: 'Biddings',
      test: (name) => name === 'BID' || name.includes('BID'),
    },
    { label: 'Invoices', test: (name) => name.includes('INVOICE') },
    {
      label: 'Products',
      test: (name) =>
        name.includes('CATEGORY') ||
        name.includes('GRADE') ||
        name.includes('PRODUCT'),
    },
    {
      label: 'Vendor',
      test: (name) => name.includes('TRADER') || name.includes('VENDOR'),
    },
    {
      label: 'Staff',
      test: (name) =>
        name === 'USER' ||
        name.includes('ROLE') ||
        name.includes('PERMISSION') ||
        name.includes('ADDRESS'),
    },
    {
      label: 'Extra',
      test: (name) =>
        name.includes('MARKET_FREEZE') || name === 'PORT' || name === 'CRM',
    },
    { label: 'User Feedback Hubs', test: (name) => name.includes('FEEDBACK') },
    { label: 'Analytics', test: (name) => name.includes('ANALYTICS') },
  ];

  const fallbackMatch = fallbackRules.find((rule) => rule.test(normalized));
  return fallbackMatch ? fallbackMatch.label : 'Other';
};

const buildMenuSections = (moduleList) => {
  const grouped = new Map();
  (moduleList || []).forEach((module) => {
    const menuLabel = module.menu || 'Other';
    if (!grouped.has(menuLabel)) grouped.set(menuLabel, []);
    grouped.get(menuLabel).push(module);
  });

  const orderedMenus = [
    ...MENU_ORDER,
    ...Array.from(grouped.keys()).filter((menu) => !MENU_ORDER.includes(menu)),
  ];

  return orderedMenus
    .filter((menu) => grouped.has(menu))
    .map((menu) => ({ label: menu, modules: grouped.get(menu) }));
};

const sortModulesByMenu = (moduleList) => {
  const menuIndex = new Map(MENU_ORDER.map((label, index) => [label, index]));
  const moduleIndex = new Map();
  MENU_DEFINITIONS.forEach((group) => {
    group.modules.forEach((moduleName, index) => {
      const normalized = normalizeModuleName(moduleName);
      if (!moduleIndex.has(normalized)) moduleIndex.set(normalized, index);
    });
  });

  return [...(moduleList || [])].sort((a, b) => {
    const aMenuIndex = menuIndex.get(a.menu) ?? menuIndex.get('Other') ?? 999;
    const bMenuIndex = menuIndex.get(b.menu) ?? menuIndex.get('Other') ?? 999;
    if (aMenuIndex !== bMenuIndex) return aMenuIndex - bMenuIndex;

    const aModuleIndex = moduleIndex.get(normalizeModuleName(a.name)) ?? 999;
    const bModuleIndex = moduleIndex.get(normalizeModuleName(b.name)) ?? 999;
    if (aModuleIndex !== bModuleIndex) return aModuleIndex - bModuleIndex;

    return a.name.localeCompare(b.name);
  });
};

const AddRole = () => {
  const { auth, setAuthData } = useContext(authContext);
  const navigate = useNavigate();
  const [roleName, setRoleName] = useState('');
  const [department, setDepartment] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [expandedModuleCard, setExpandedModuleCard] = useState(null);

  const fetchPermissions = async () => {
    try {
      setLoading(true);
      setError(null);

      const userData = JSON.parse(localStorage.getItem(APP_TOKEN));

      if (!userData || !userData.token) {
        setError('Authentication token not found');
        setLoading(false);
        return;
      }

      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

      console.log('🔧 Fetching permissions from:', graphqlEndpoint);

      const graphqlPayload = {
        query: `query getPermissions($filter: [FilterMap], $pageNo: Int, $pageSize: Int, $sortCol: String, $sortDirection: String, $pagination: Boolean) {
          permissions(filter: $filter, pageNo: $pageNo, pageSize: $pageSize, sortCol: $sortCol, sortDirection: $sortDirection, pagination: $pagination) {
            action
            permission {
              id
              name
            }
            permissions {
              id
              name
              module
              description
              createdAt
            }
            totalPages
            totalCount
          }
        }`,
        variables: {
          pageNo: 1,
          pageSize: 200,
          sortCol: 'name',
          sortDirection: 'ASC',
          pagination: true,
        },
      };

      const response = await axios.post(graphqlEndpoint, graphqlPayload, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.token}`,
        },
      });

      console.log('✅ API Response:', response.data);

      if (
        response.status === 200 &&
        response.data?.data?.permissions?.permissions
      ) {
        const permissionsData = response.data.data.permissions.permissions;
        const visiblePermissions = permissionsData.filter(
          (permission) => !shouldHidePermission(permission?.name),
        );

        // Group permissions by module
        const groupedByModule = {};

        visiblePermissions.forEach((permission) => {
          const moduleName = permission.module || 'OTHER';

          if (!groupedByModule[moduleName]) {
            groupedByModule[moduleName] = [];
          }

          groupedByModule[moduleName].push({
            id: permission.id,
            name: permission.name,
            description: permission.description,
          });
        });

        // Convert to array format for rendering
        const modulesArray = Object.keys(groupedByModule).map((moduleName) => ({
          name: moduleName,
          menu: resolveMenuForModule(moduleName),
          permissions: groupedByModule[moduleName],
        }));

        const sortedModules = sortModulesByMenu(modulesArray);

        console.log('✅ Grouped Modules:', modulesArray);
        setModules(sortedModules);
      } else if (response.data?.errors) {
        console.error('❌ GraphQL Errors:', response.data.errors);
        setError(
          response.data.errors[0]?.message || 'Failed to fetch permissions',
        );
      } else {
        setError('Failed to fetch permissions');
      }
    } catch (err) {
      console.error('❌ Error fetching permissions:', err);

      if (err.response) {
        console.error('Server Error:', err.response.data);
        setError(
          err.response.data?.errors?.[0]?.message ||
          err.response.data?.message ||
          'Server error occurred',
        );
      } else if (err.request) {
        console.error('Network Error:', err.request);
        setError('Network error - Unable to reach server');
      } else {
        console.error('Error:', err.message);
        setError(err.message || 'An error occurred while fetching permissions');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPermissions();
  }, []);

  const handleCheckboxChange = (permissionId) => {
    setSelectedPermissions((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((id) => id !== permissionId);
      }
      return [...prev, permissionId];
    });
  };

  // Handle Select All for a specific module
  const handleModuleSelectAll = (module) => {
    const modulePermissionIds = module.permissions.map((p) => p.id);
    const allSelected = modulePermissionIds.every((id) =>
      selectedPermissions.includes(id),
    );

    if (allSelected) {
      // Deselect all permissions from this module
      setSelectedPermissions((prev) =>
        prev.filter((id) => !modulePermissionIds.includes(id)),
      );
    } else {
      // Select all permissions from this module
      setSelectedPermissions((prev) => {
        const newPermissions = [...prev];
        modulePermissionIds.forEach((id) => {
          if (!newPermissions.includes(id)) {
            newPermissions.push(id);
          }
        });
        return newPermissions;
      });
    }
  };

  // Check if all permissions in a module are selected
  const isModuleFullySelected = (module) => {
    const modulePermissionIds = module.permissions.map((p) => p.id);
    return (
      modulePermissionIds.length > 0 &&
      modulePermissionIds.every((id) => selectedPermissions.includes(id))
    );
  };

  // Check if some (but not all) permissions in a module are selected
  const isModulePartiallySelected = (module) => {
    const modulePermissionIds = module.permissions.map((p) => p.id);
    const selectedCount = modulePermissionIds.filter((id) =>
      selectedPermissions.includes(id),
    ).length;
    return selectedCount > 0 && selectedCount < modulePermissionIds.length;
  };

  const handleBackToRolesList = () => {
    navigate('/staff/edit-list');
  };

  const handleCreate = async () => {
    // Validation
    if (!roleName.trim()) {
      toast.error('Please enter a role name');
      return;
    }

    if (selectedPermissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }

    try {
      setCreating(true);
      const userData = JSON.parse(localStorage.getItem(APP_TOKEN));

      if (!userData || !userData.token) {
        toast.error('Authentication token not found');
        return;
      }

      const graphqlEndpoint = process.env.REACT_APP_GRAPHQL_ENDPOINT;

      console.log('🔧 Creating role:', roleName);
      console.log('🔧 Selected permissions:', selectedPermissions);

      const createMutation = {
        query: `mutation CreateRole($role: String!, $department: Department, $permissions: [ID!]!) {
          createRole(role: $role, department: $department, permissions: $permissions)
        }`,
        variables: {
          role: roleName.trim(),
          ...(department && { department }), // Only include if department is selected
          permissions: selectedPermissions,
        },
      };

      const response = await axios.post(graphqlEndpoint, createMutation, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userData.token}`,
        },
      });

      console.log('✅ Create Role Response:', response.data);

      if (response.status === 200 && response.data?.data?.createRole) {
        console.log('✅ Role created successfully');
        await syncUserPermissions(auth?.data || userData, setAuthData);
        toast.success('Role created successfully!');
        navigate('/staff/edit-list');
      } else if (response.data?.errors) {
        console.error('❌ GraphQL Errors:', response.data.errors);
        toast.error(
          response.data.errors[0]?.message || 'Failed to create role',
        );
      } else {
        toast.error('Failed to create role');
      }
    } catch (err) {
      console.error('❌ Error creating role:', err);
      const errorMsg =
        err.response?.data?.errors?.[0]?.message ||
        err.response?.data?.message ||
        err.message;
      toast.error(`Error creating role: ${errorMsg}`);
    } finally {
      setCreating(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div
        className="p-4"
        style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}
      >
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: '50vh' }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="p-4 role-form-page role-add-page"
        style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}
      >
        <div className="d-flex justify-content-between align-items-center mb-4 role-form-header">
          <div className="d-flex align-items-center gap-3">
            <button
              type="button"
              className="btn btn-outline-secondary d-flex align-items-center gap-2"
              onClick={handleBackToRolesList}
              aria-label="Back to roles list"
            >
              <ArrowLeft size={18} aria-hidden="true" />
              <span>Back</span>
            </button>
            <h4 className="mb-0">Create Role</h4>
          </div>
        </div>
        <div className="alert alert-danger" role="alert">
          <h5 className="alert-heading">Error</h5>
          <p>{error}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={fetchPermissions}
            aria-label="Retry fetching permissions"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="p-4 role-form-page role-add-page"
      style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}
    >
      <div className="d-flex justify-content-between align-items-center mb-4 role-form-header">
        <div className="d-flex align-items-center gap-3">
          <button
            type="button"
            className="btn btn-outline-secondary d-flex align-items-center gap-2"
            onClick={handleBackToRolesList}
            aria-label="Back to roles list"
          >
            <ArrowLeft size={18} aria-hidden="true" />
            <span>Back</span>
          </button>
          <h4 className="mb-0">Create Role</h4>
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="mb-3">
            <label htmlFor="roleName" className="form-label">
              Name <span className="text-danger">*</span>
            </label>
            <input
              id="roleName"
              type="text"
              className="form-control"
              placeholder="Enter Role Name"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              aria-required="true"
              aria-label="Role name"
            />
          </div>
          <div className="mb-0">
            <label htmlFor="department" className="form-label">
              Department
            </label>
            <select
              id="department"
              className="form-control"
              value={department}
              onChange={(e) => setDepartment(e.target.value)}
              aria-label="Department"
            >
              <option value="">Select Department</option>
              {DEPARTMENT_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="card border-0 shadow-sm">
        <div className="card-body p-4">
          <h6 className="mb-3" id="permissions-heading">
            Assign Project related Permission to Roles
          </h6>

          {modules.length === 0 ? (
            <div className="text-center py-5">
              <p className="text-muted">No permissions available</p>
            </div>
          ) : (
            <>
              <div className="role-permissions-mobile">
                {buildMenuSections(modules).map((section) => (
                  <div className="role-mobile-section" key={section.label}>
                    <div className="role-mobile-section__title">
                      {section.label}
                    </div>
                    {section.modules.map((module) => {
                      const cardId = `${section.label}-${module.name}`;
                      const isExpanded = expandedModuleCard === cardId;
                      const selectedCount = module.permissions.filter(
                        (permission) =>
                          selectedPermissions.includes(permission.id),
                      ).length;
                      return (
                        <div className="role-permission-card" key={cardId}>
                          <div className="role-permission-card__summary">
                            <div className="role-permission-card__module">
                              <IndeterminateCheckbox
                                className="form-check-input"
                                type="checkbox"
                                id={`mobile-module-${module.name}`}
                                checked={isModuleFullySelected(module)}
                                indeterminate={isModulePartiallySelected(
                                  module,
                                )}
                                onChange={() => handleModuleSelectAll(module)}
                                aria-label={`Select all permissions for ${module.name} module`}
                              />
                              <label htmlFor={`mobile-module-${module.name}`}>
                                {module.name}
                              </label>
                            </div>
                            <div className="role-permission-card__meta">
                              <span>
                                {selectedCount}/{module.permissions.length}
                              </span>
                              <button
                                type="button"
                                className={`role-permission-card__toggle ${isExpanded ? 'is-open' : ''
                                  }`}
                                onClick={() => {
                                  setExpandedModuleCard((prevId) => {
                                    return prevId === cardId ? null : cardId;
                                  });
                                }}
                                aria-expanded={isExpanded}
                                aria-label={
                                  isExpanded
                                    ? `Collapse ${module.name} permissions`
                                    : `Expand ${module.name} permissions`
                                }
                              >
                                <ChevronDown size={14} />
                              </button>
                            </div>
                          </div>
                          {isExpanded ? (
                            <div className="role-permission-card__permissions">
                              {module.permissions.map((permission) => (
                                <label
                                  className="role-permission-option"
                                  key={permission.id}
                                  title={permission.description}
                                >
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    checked={selectedPermissions.includes(
                                      permission.id,
                                    )}
                                    onChange={() =>
                                      handleCheckboxChange(permission.id)
                                    }
                                  />
                                  <span>{permission.name}</span>
                                </label>
                              ))}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
              <div className="table-responsive role-permissions-desktop">
                <table className="table" aria-labelledby="permissions-heading">
                  <thead style={{ backgroundColor: '#f8f9fa' }}>
                    <tr>
                      <th className="border-0 py-3" scope="col">
                        Menu
                      </th>
                      <th className="border-0 py-3" scope="col">
                        Module
                      </th>
                      <th className="border-0 py-3" scope="col">
                        Permissions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const sections = buildMenuSections(modules);
                      let rowIndex = 0;
                      return sections.flatMap((section) =>
                        section.modules.map((module, moduleIndex) => {
                          const isEven = rowIndex % 2 === 0;
                          rowIndex += 1;
                          return (
                            <tr
                              key={`${section.label}-${module.name}`}
                              style={{
                                backgroundColor: isEven ? '#f8f9fa' : 'white',
                              }}
                            >
                              {moduleIndex === 0 ? (
                                <td
                                  className="py-3 align-top"
                                  rowSpan={section.modules.length}
                                >
                                  <strong>{section.label}</strong>
                                </td>
                              ) : null}
                              <td className="py-3 align-top">
                                <div className="d-flex align-items-center gap-2">
                                  <IndeterminateCheckbox
                                    className="form-check-input"
                                    type="checkbox"
                                    id={`module-${module.name}`}
                                    checked={isModuleFullySelected(module)}
                                    indeterminate={isModulePartiallySelected(
                                      module,
                                    )}
                                    onChange={() =>
                                      handleModuleSelectAll(module)
                                    }
                                    style={{ cursor: 'pointer' }}
                                    aria-label={`Select all permissions for ${module.name} module`}
                                  />
                                  <label
                                    htmlFor={`module-${module.name}`}
                                    style={{
                                      cursor: 'pointer',
                                      marginBottom: 0,
                                    }}
                                  >
                                    <strong>{module.name}</strong>
                                  </label>
                                </div>
                              </td>
                              <td className="py-3">
                                <div className="d-flex gap-3 flex-wrap">
                                  {module.permissions.map((permission) => (
                                    <div
                                      key={permission.id}
                                      className="form-check"
                                    >
                                      <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`permission-${permission.id}`}
                                        checked={selectedPermissions.includes(
                                          permission.id,
                                        )}
                                        onChange={() =>
                                          handleCheckboxChange(permission.id)
                                        }
                                        aria-label={`Select permission: ${permission.name}`}
                                      />
                                      <label
                                        className="form-check-label"
                                        htmlFor={`permission-${permission.id}`}
                                        title={permission.description}
                                        style={{ cursor: 'pointer' }}
                                      >
                                        {permission.name}
                                      </label>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          );
                        }),
                      );
                    })()}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="text-end mt-3 role-form-footer">
        <button
          type="button"
          className="btn btn-primary role-form-primary-btn role-form-primary-btn--footer"
          onClick={handleCreate}
          disabled={creating}
          aria-label={creating ? 'Creating role...' : 'Create role'}
        >
          {creating ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Creating...
            </>
          ) : (
            'Create'
          )}
        </button>
      </div>
    </div>
  );
};

export default AddRole;
